import { useEffect, useState, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { CLRM } from "@/models/clrm";
import { validateEffectiveStartDateForProgramYear } from "@/utils/dates";

type Option = {
  Text: string;
  Value: string;
};

type CareTypeBase = "ProgramTypeOffered";
type DurationBase = "ProgramDurationOffered";
type LanguageBase = "LanguageUsedInClassroom";
type ContractTypeBase = "ContractTypeOffered";

export function getMostRecentClassroom(records: CLRM[]): CLRM | undefined {
  if (!records || records.length === 0) return undefined;
  return [...records].sort((a, b) => {
    const da = new Date(a.EffectiveStartDate ?? "").getTime();
    const db = new Date(b.EffectiveStartDate ?? "").getTime();
    return (
      (Number.isNaN(db) ? -Infinity : db) - (Number.isNaN(da) ? -Infinity : da)
    );
  })[0];
}

export function buildClassroomAddDefaults(record: CLRM): Partial<CLRM> {
  return {
    ReportingLEA: record.ReportingLEA,
    PreschoolCode: record.PreschoolCode,
    ProgramYear: record.ProgramYear,
    LocalClassroomId: record.LocalClassroomId,
    ClassroomName: record.ClassroomName,
    VolunteerFrequency: record.VolunteerFrequency,
    ProgramTypeOffered1: record.ProgramTypeOffered1,
    ProgramTypeOffered2: record.ProgramTypeOffered2,
    ProgramDurationOffered1: record.ProgramDurationOffered1,
    ProgramDurationOffered2: record.ProgramDurationOffered2,
    ContractTypeOffered1: record.ContractTypeOffered1,
    ContractTypeOffered2: record.ContractTypeOffered2,
    SettingTypeOffered: record.SettingTypeOffered,
    MHCSOffered: record.MHCSOffered,
    LanguageProgramType: record.LanguageProgramType,
    LanguageUsedInClassroom1: record.LanguageUsedInClassroom1,
    LanguageUsedInClassroom2: record.LanguageUsedInClassroom2,
    LanguageUsedInClassroom3: record.LanguageUsedInClassroom3,
    LanguageUsedInClassroom4: record.LanguageUsedInClassroom4,
    LanguageUsedInClassroom5: record.LanguageUsedInClassroom5,
    Id: "",
    EffectiveStartDate: "",
    EffectiveEndDate: "",
  };
}

export function useCLRMFormLogic({
  form,
  classroom,
  resetKey,
  careTypeOptions,
  durationOptions,
  languageOptions,
  contractTypeOptions,
}: {
  form: UseFormReturn<CLRM, any, CLRM>;
  classroom: CLRM;
  resetKey: string;
  careTypeOptions: Option[];
  durationOptions: Option[];
  languageOptions: Option[];
  contractTypeOptions: Option[];
}) {
  const { setValue, getValues, trigger, watch } = form;

  const [careTypeValues, setCareTypeValues] = useState<string[]>([]);
  const [durationValues, setDurationValues] = useState<string[]>([]);
  const [languageValues, setLanguageValues] = useState<string[]>([]);
  const [contractTypeValues, setContractTypeValues] = useState<string[]>([]);
  const userHasInteractedWithDate = useRef(false);

  const getN = (n: number, ...values: (string | undefined)[]) =>
    values
      .slice(0, n)
      .filter((x): x is string => typeof x === "string" && x.trim().length > 0);

  const valuesToOptions = (values: string[], allOptions: Option[]) => {
    const set = new Set(values);
    return allOptions.filter((x) => set.has(x.Value));
  };

  const optionsToValues = (items: Array<Option>) =>
    items
      .map((x) => x.Value)
      .filter((v): v is string => typeof v === "string" && v.length > 0);

  const writeBackN = (
    base: CareTypeBase | DurationBase | LanguageBase | ContractTypeBase,
    values: string[],
    maxFields: number
  ) => {
    for (let i = 1; i <= maxFields; i++) {
      const value = values[i - 1] ?? "";
      setValue(`${base}${i}` as any, value as any, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  useEffect(() => {
    setCareTypeValues(
      getN(
        2,
        (classroom as any).ProgramTypeOffered1,
        (classroom as any).ProgramTypeOffered2
      )
    );

    setDurationValues(
      getN(
        2,
        (classroom as any).ProgramDurationOffered1,
        (classroom as any).ProgramDurationOffered2
      )
    );

    setLanguageValues(
      getN(
        5,
        (classroom as any).LanguageUsedInClassroom1,
        (classroom as any).LanguageUsedInClassroom2,
        (classroom as any).LanguageUsedInClassroom3,
        (classroom as any).LanguageUsedInClassroom4,
        (classroom as any).LanguageUsedInClassroom5
      )
    );

    setContractTypeValues(
      getN(
        2,
        (classroom as any).ContractTypeOffered1,
        (classroom as any).ContractTypeOffered2
      )
    );
    userHasInteractedWithDate.current = !!classroom.EffectiveStartDate;
  }, [resetKey]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "EffectiveStartDate") {
        userHasInteractedWithDate.current = true;
      }
      if (
        name === "ProgramYear" &&
        value.EffectiveStartDate &&
        userHasInteractedWithDate.current
      ) {
        trigger("EffectiveStartDate");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  const validateEffectiveStartDate = (
    value: string | undefined
  ): string | true => {
    return validateEffectiveStartDateForProgramYear(
      value,
      getValues("ProgramYear")
    );
  };

  return {
    careTypes: {
      value: valuesToOptions(careTypeValues, careTypeOptions),
      onChange: (items: Option[]) => {
        const next = optionsToValues(items).slice(0, 2);
        setCareTypeValues(next);
        writeBackN("ProgramTypeOffered", next, 2);
      },
    },
    programDurations: {
      value: valuesToOptions(durationValues, durationOptions),
      onChange: (items: Option[]) => {
        const next = optionsToValues(items).slice(0, 2);
        setDurationValues(next);
        writeBackN("ProgramDurationOffered", next, 2);
      },
    },
    languages: {
      value: valuesToOptions(languageValues, languageOptions),
      onChange: (items: Option[]) => {
        const next = optionsToValues(items).slice(0, 5);
        setLanguageValues(next);
        writeBackN("LanguageUsedInClassroom", next, 5);
      },
    },
    contractTypes: {
      value: valuesToOptions(contractTypeValues, contractTypeOptions),
      onChange: (items: Option[]) => {
        const next = optionsToValues(items).slice(0, 2);
        setContractTypeValues(next);
        writeBackN("ContractTypeOffered", next, 2);
      },
    },
    validateEffectiveStartDate,
  };
}
